"use client";

import { useEffect, useState } from "react";
import { getHeaderData } from "./header-service";
import { HeaderData } from "./types";
import Ribbon from "./ribbon/ribbon";
import HeaderForSmallScreens from "./header-sm";
import HeaderForLargeScreens from "./header-lg";
import { DEVICE_RESOLUTION } from "../../app-constants";
import { HeaderContext } from "./headerContextProvider";
import { ToastProvider } from "../../components/ToastNotification/toastProvider";
import UserImpersonate from "./userImpersonate";
import { Loading } from "./loader";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { publishUserData, setHeaderIsLoading, setIsRafEnabled } from "../../redux/features/header/headerSlice";
import { useUserData } from "./hooks";
import { publishModalVisibility } from "../../redux/features/modal/modalSlice";
import Modal from "../../components/Modal/modal";
import { setActiveCompetitions } from "../../redux/features/categories/actions";

/* Read the server-set ribbon flag once at module load — synchronous, before any render.
   Razor emits window.__BOTB_RIBBON_ENABLED__ in HeaderV2.cshtml based on whether
   the Umbraco "topBannerContent" property is populated on the homepage node.
   Defaults to true so a missing variable (e.g. test environments) is safe. */
const ribbonPreEnabled: boolean = typeof window !== "undefined" ? (window.__BOTB_RIBBON_ENABLED__ ?? true) : true;

const Header = ({
  isBotBPassHeader,
  isFounderPage,
  isLeaderBoardPage,
  isLeaderBoardChristmasPage,
  milestoneData,
  renderModalComponentHere = true,
}: {
  isBotBPassHeader?: boolean;
  isFounderPage?: boolean;
  isLeaderBoardPage?: boolean;
  isLeaderBoardChristmasPage?: boolean;
  milestoneData?: string;
  renderModalComponentHere?: boolean;
}) => {
  const [header, setHeader] = useState<HeaderData>();
  const [windowInnerWidth, setWindowInnerWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useAppDispatch();
  let userData = useUserData();
  const buyYourTicketsModalData = useAppSelector(state => state.modal.data);
  const isBuyTicketModalVisible = useAppSelector(state => state.modal.isOpen);
  const withOverlay = useAppSelector(state => state.modal.withOverlay);

  const blackFridayLeaderboardNavMenu = true;

  useEffect(() => {
    const activeCompetitionsPromise = dispatch(setActiveCompetitions());
    activeCompetitionsPromise
      .unwrap()
      .then(res => {
        const syncEvent = new CustomEvent("sync-header-store", {
          detail: res,
        });
        window.dispatchEvent(syncEvent);
      })
      .catch(err => console.error(err));

    return () => {
      activeCompetitionsPromise.abort();
    };
  }, []);

  const fetchHeader = async () => {
    try {
      const response = await getHeaderData();
      const headerData = (await response.json()) as HeaderData;
      dispatch(setIsRafEnabled(headerData.IsRAFEnabled));
      const syncStoresEvent = new CustomEvent("sync-second-store", {
        detail: headerData,
      });
      window.dispatchEvent(syncStoresEvent);
      setHeader(headerData);
      dispatch(publishUserData({ userData: headerData?.MyAccountMenuBar }));
    } catch (err) {
      console.log(`Error while fetching the header data - ${JSON.stringify(err)}`);
    } finally {
      setIsLoading(false);
      dispatch(setHeaderIsLoading(false));
    }
  };

  useEffect(() => {
    fetchHeader();
    renderRespectiveLayout();
    window.addEventListener("resize", renderRespectiveLayout);
  }, []);

  const renderRespectiveLayout = () => {
    setWindowInnerWidth(window.innerWidth);
  };

  const getHeaderForLargeScreens = () => {
    if (isLoading) {
      return (
        <Loading
          isDesktop={true}
          isLeaderBoardPage={isLeaderBoardPage}
          isLeaderBoardChristmasPage={isLeaderBoardChristmasPage}
        />
      );
    }
    return (
      <HeaderForLargeScreens
        accountValidationContent={header?.AccountValidationContent}
        navMenus={header?.TopNavLinks}
        showPastDueBanner={header?.ShowPastDueBanner}
        onSnoozePastDue={fetchHeader}
        showCancelationBanner={header?.ShowCancelationBanner}
        onSnoozeCancelation={fetchHeader}
        userData={userData}
        botbPassNavMenu={header?.BotBPassNav}
        socialContentTop={header?.SocialContentTop}
        IsRAFEnabled={header?.IsRAFEnabled}
        isBotBPassHeader={isBotBPassHeader}
        isFounderPage={isFounderPage}
        isLeaderBoardPage={isLeaderBoardPage}
        isLeaderBoardChristmasPage={isLeaderBoardChristmasPage}
        milestoneData={milestoneData}
        blackFridayLeaderboardNavMenu={blackFridayLeaderboardNavMenu}
      />
    );
  };

  const getHeaderForMobileScreens = () => {
    if (typeof document === "undefined") {
      return <Loading isDesktop={false} />;
    }

    const isLoginPage = document.querySelector(".pp-doctype-loginpage") !== null;
    const isSignUpPage = document.querySelector(".pp-doctype-registrationpage") !== null;
    if (!isLoginPage && !isSignUpPage && isLoading) {
      return <Loading isDesktop={false} />;
    }
    return (
      <HeaderForSmallScreens
        botbPassNavMenu={header?.BotBPassNav}
        topNavCTA={header?.TopNavCta}
        showPastDueBanner={header?.ShowPastDueBanner}
        onSnoozePastDue={fetchHeader}
        showCancelationBanner={header?.ShowCancelationBanner}
        onSnoozeCancelation={fetchHeader}
        IsRAFEnabled={header?.IsRAFEnabled}
        userData={userData}
        accountValidationContent={header?.AccountValidationContent}
        accountNavData={header?.MyAccountNav}
        isBotBPassHeader={isBotBPassHeader}
        isFounderPage={isFounderPage}
        isLeaderBoardPage={isLeaderBoardPage}
        isLeaderBoardChristmasPage={isLeaderBoardChristmasPage}
        milestoneData={milestoneData}
        blackFridayLeaderboardNavMenu={blackFridayLeaderboardNavMenu}
      />
    );
  };

  const userAgent = typeof window !== "undefined" ? window.navigator.userAgent : "";
  const isWebViewRequestedByMobileApp =
    userAgent.includes("botb_app_id=") ||
    userAgent.includes("Darwin/") ||
    userAgent.includes("mobile-app-") ||
    userAgent.includes("okhttp/");

  return (
    !isWebViewRequestedByMobileApp &&
    !header?.IsMobileApp && (
      <HeaderContext.Provider
        value={{
          EasyWinPageUrl: header?.MyAccountMenuBar?.Basket?.EasyWinPageUrl,
          ShouldEnableSpotTheBallTestOption: header?.MyAccountMenuBar?.Basket?.ShouldEnableSpotTheBallTestOption,
          CheckoutURL: header?.MyAccountMenuBar?.Basket?.CheckoutUrl,
          TierClass: header?.MyAccountMenuBar?.TierClass,
        }}>
        {header?.ImpersonationDetails && (
          <UserImpersonate
            userProfileName={header?.ImpersonationDetails.UserProfileName}
            impersonatorName={header?.ImpersonationDetails.ImpersonatorName}
          />
        )}
        {/* Ribbon slot: space reservation is server-authoritative.
            ribbonPreEnabled is set synchronously from window.__BOTB_RIBBON_ENABLED__,
            which Razor emits in HeaderV2.cshtml based on the Umbraco topBannerContent property.

            While the header API is in-flight (isLoading):
              - ribbon enabled  → show 40 px black placeholder; seamlessly continues the SSR
                                  placeholder that was pre-rendered in the server HTML
              - ribbon disabled → render nothing; no space reserved, no layout shift

            Once the API resolves:
              - ribbon has content  → real <Ribbon> fills the same 40 px slot → zero shift
              - ribbon has no content → null → nothing shown */}
        {!isBotBPassHeader &&
          (isLoading ? (
            ribbonPreEnabled ? (
              <div aria-hidden="true" style={{ height: "40px", background: "#000" }} />
            ) : null
          ) : header?.ShouldShowRibbon && header?.TopBannerData?.TopBannerContent ? (
            <Ribbon topBannerData={header.TopBannerData} />
          ) : null)}
        <nav>
          {windowInnerWidth >= DEVICE_RESOLUTION.Desktop ? getHeaderForLargeScreens() : getHeaderForMobileScreens()}
        </nav>
        <ToastProvider />
        {renderModalComponentHere ? (
          <Modal
            isOpen={isBuyTicketModalVisible}
            title=""
            closeModal={event => {
              event.stopPropagation();
              dispatch(publishModalVisibility({ isOpen: false }));
            }}
            withOverlay={withOverlay}
            modalClassNames={`md:rounded-2xl xs:absolute xs:bottom-0 xs:w-full xs:rounded-b-none xs:rounded-t-2xl z-10 !max-h-none ${isBuyTicketModalVisible ? "!px-4 pt-12 pb-8 max-lg:!pb-4 lg:!px-8 lg:!pb-10 lg:!pt-12" : ""}`}
            body={buyYourTicketsModalData}
          />
        ) : null}
      </HeaderContext.Provider>
    )
  );
};

export default Header;
